import React, {Fragment} from 'react';
import {
    EditButton,
    FunctionField,
    ListButton,
    LongTextInput,
    SelectInput,
    ShowButton,
    SimpleForm,
    SimpleShowLayout,
    TextField,
    TextInput,
    required
} from 'admin-on-rest';
import {RefreshButton} from 'admin-on-rest/lib/mui/button';
import {DependentInput} from 'aor-dependent-input';
import ReactTooltip from 'react-tooltip';
import Edit from '../admin/Edit';
import Show from '../admin/Show';
import FormattedDateTextInput from '../components/FormattedDateTextInput';
import ScheduleReportFormDaysBackInput from '../components/ScheduleReportFormDaysBackInput';
import SecondaryFilterFieldsWithLocations from '../components/SecondaryFilterFieldsWithLocations';
import TooltipIcon from '../components/TooltipIcon';
import {convertTimeFromHoursMinutes, validateEmailArray} from '../shared/functions';
import {HOURS_24_AMPM, TIME_ZONE_OFFSETS, WEEKDAYS} from '../shared/dateTimeLists';
import {viewStyles} from '../shared/styles-view';
import Avatar from 'material-ui/Avatar';
import {CardActions} from 'material-ui/Card';
import Chip from 'material-ui/Chip';
import Icon from 'material-ui/svg-icons/action/schedule';

export const fuseScheduledReportsIcon = Icon;

const getRunTime = (record) => {
    let runHour = (record && record.run_time) ? record.run_time.substr(0, 2) : null;
    return (runHour) ? convertTimeFromHoursMinutes(runHour, 0) : '';
};

// View: Show ::::::::::::::::::::::::::::::

const ShowTitle = ({record}) => {
    return (
        <span>
            <Avatar icon={<Icon/>} style={viewStyles.avatar} className="avatar-title"/>
            {record ? `${record.name}` : 'Scheduled Report'}
        </span>
    );
};

const ShowDescription = () => {
    return (
        <span>
            <p>Review Scheduled Report details below. To edit this Scheduled Report's settings, click <strong>Edit</strong> above.</p>
        </span>
    );
};

const ShowActions = ({basePath, data, canEdit}) => (
    <CardActions style={viewStyles.actions}>
        <ListButton basePath={basePath}/>
        {canEdit &&
            <EditButton basePath={basePath} record={data}/>
        }
        <RefreshButton/>
    </CardActions>
);

// Display Frequency and Runtime fields
const ShowFrequencyFields = ({record}) => {
    let weekday = (record && record.report_day) ? WEEKDAYS.find(r => r.id === record.report_day) : null;
    let runTime = getRunTime(record);
    return (
        <Fragment>
            <hr style={{...viewStyles.break, ...viewStyles.marginBottom}}/>
            <span style={viewStyles.fieldLabel}>Frequency</span>
            <span>Report runs {record.frequency} </span>
            {weekday &&
                <span>on {weekday.name} </span>
            }
            <span> at {runTime}.</span>
        </Fragment>
    );
};

// Display Specific or Relative Date Range fields
const ShowRangeFields = ({record}) => {
    const dateRange = (record && record.payload && record.payload.dateRange) ? record.payload.dateRange : null;
    return (
        <Fragment>
            {dateRange &&
                <Fragment>
                    <hr style={{...viewStyles.break, ...viewStyles.marginBottom}}/>
                    <span style={viewStyles.fieldLabel}>Date Range</span>
                    {dateRange.daysBack === 0 &&
                        <Fragment>
                            <span>Report data ranges from {dateRange.fromDay} to {dateRange.toDay} with
                                a time zone offset of {dateRange.tz}.</span>
                        </Fragment>
                    }
                    {dateRange.daysBack > 0 &&
                        <Fragment>
                            <span>Report data ranges from runtime to {dateRange.daysBack} days
                                back, with a time zone offset of {dateRange.tz}.</span>
                        </Fragment>
                    }
                </Fragment>
            }
        </Fragment>
    );
};

// Display Secondary Filters if present
const ShowFilterFields = ({record}) => {
    const location = (record && record.payload && record.payload.location) ? record.payload.location : null;
    const cities = (location && location.cities && location.cities.length > 0) ? location.cities : null;
    const dbas = (location && location.dbas && location.dbas.length > 0) ? location.dbas : null;
    const districts = (location && location.districts && location.districts.length > 0) ? location.districts : null;
    const states = (location && location.states && location.states.length > 0) ? location.states : null;
    const locations = (location && location.locations && location.locations.length > 0) ? location.locations : null;
    return (
        <Fragment>
            {(cities || dbas || districts || states || locations) &&
                <Fragment>
                    <hr style={{...viewStyles.break, ...viewStyles.marginBottom}}/>
                </Fragment>
            }
            {dbas &&
                <Fragment>
                    <span style={viewStyles.fieldLabel}>Filtered by DBA IDs:</span>
                    <FunctionField label="" render={() => renderShowArray(dbas, 'dba', null)}/>
                </Fragment>
            }
            {districts &&
                <Fragment>
                    <span style={viewStyles.fieldLabel}>Filtered by District IDs:</span>
                    <FunctionField label="" render={() => renderShowArray(districts, 'district', null)}/>
                </Fragment>
            }
            {states &&
                <Fragment>
                    <span style={viewStyles.fieldLabel}>Filtered by States:</span>
                    <FunctionField label="" render={() => renderShowArray(states, 'state', null)}/>
                </Fragment>
            }
            {cities &&
                <Fragment>
                    <span style={viewStyles.fieldLabel}>Filtered by Cities:</span>
                    <FunctionField label="" render={() => renderShowArray(cities, 'city', null)}/>
                </Fragment>
            }
            {locations &&
                <Fragment>
                    <span style={viewStyles.fieldLabel}>Filtered by Location IDs:</span>
                    <FunctionField label="" render={() => renderShowArray(locations, 'location', null)}/>
                </Fragment>
            }
        </Fragment>
    );
};

// Display summary of next and last runtime
const ShowSummary = ({record}) => {
    const lastRan = (record && record.last_ran) ? record.last_ran : null;
    let lastRanStr = '';
    if (lastRan && lastRan.indexOf('0001') !== 0) {
        let lastRanDate = new Date(lastRan);
        lastRanStr = lastRanDate.toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'});
    }
    let runTime = getRunTime(record);
    const expiresDate = (record && record.expires) ? new Date(record.expires) : null;
    let expiresStr = '';
    if (expiresDate) {
        let expiresDateStr = expiresDate.toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'});
        let now = new Date();
        expiresStr = (expiresDate.getTime() <= now.getTime())
            ? 'This Report expired on ' + expiresDateStr + '.'
            : 'This Report will expire on ' + expiresDateStr + '.';
    }
    return (
        <Fragment>
            {lastRan && lastRanStr !== '' &&
                <Fragment>
                    <hr style={{...viewStyles.break, ...viewStyles.marginBottom}}/>
                    <span style={viewStyles.fieldLabel}>Report Last Run</span>
                    <span>This Report was last run on {lastRanStr} at {runTime}.</span>
                </Fragment>
            }
            {expiresDate && expiresStr !== '' &&
                <Fragment>
                    <hr style={{...viewStyles.break, ...viewStyles.marginBottom}}/>
                    <span style={viewStyles.fieldLabel}>Report Expires</span>
                    <span>{expiresStr}</span>
                </Fragment>
            }
        </Fragment>
    )
};

const renderShowArray = (list, type, key) => {
    if (list && list.length > 0) {
        return (
            <Fragment>
                {list.map((item) => <Chip
                    key={`${type}-${item}`}
                    label={(key) ? item[key] : item}
                    style={viewStyles.chip}>
                    {(key) ? item[key] : item}
                </Chip>)}
            </Fragment>
        )
    }
    return (
        <Fragment>
            {[].map(() => <span/>)}
        </Fragment>
    )
};

// Display Email list as Chips
const renderShowEmails = (record) => {
    const emails = (record && record.emails) ? record.emails : null;
    if (emails && emails.length > 0) {
        return (
            <Fragment>
                {emails.map((email) => <Chip key={`email-${email}`} label={email}
                                             style={viewStyles.chip}>{email}</Chip>)}
            </Fragment>
        )
    }
    return (
        <Fragment>
            {[].map(() => <span/>)}
        </Fragment>
    )
};

/**
 * View: Scheduled Report Show
 *
 * @param canEdit
 * @param record
 * @param props
 * @returns {JSX.Element}
 */
export const viewScheduledReportShow = ({canEdit, record, ...props}) => (
    <Show
        {...props} title={<ShowTitle/>} desc={<ShowDescription/>}
        actions={<ShowActions basePath="/ScheduledReports" canEdit={true}/>}
    >
        <SimpleShowLayout>
            <TextField source="name" label="Report Name"/>
            <ShowFrequencyFields/>
            <ShowRangeFields/>
            <ShowFilterFields/>
            <hr style={{...viewStyles.break, ...viewStyles.marginBottom}}/>
            <span style={viewStyles.fieldLabel}>Emails</span>
            <FunctionField addLabel={false} render={(record) => renderShowEmails(record)}/>
            <ShowSummary/>
        </SimpleShowLayout>
    </Show>
);

// View: Edit ::::::::::::::::::::::::::::::

const EditTitle = ({record}) => {
    return (
        <span>
            <Avatar icon={<Icon/>} style={viewStyles.avatar} className="avatar-title"/>
            Edit {record ? `${record.name}` : 'Scheduled Report'}
        </span>
    );
};

const EditDescription = () => {
    return (
        <span>
            <p>Modify Scheduled Report settings below. Click <strong>Save</strong> to store this Scheduled Report.</p>
        </span>
    );
};

const EditActions = ({basePath, data, canEdit}) => (
    <CardActions style={viewStyles.actions}>
        <ListButton basePath={basePath}/>
        <ShowButton basePath={basePath} record={data}/>
        <RefreshButton/>
    </CardActions>
);

const hasSecondaryFilter = (filterTypes) => filterTypes !== 'all' && filterTypes !== '';
const lacksSecondaryFilter = (filterTypes) => filterTypes === 'all' || filterTypes === '';

/**
 * View: Scheduled Report Edit
 *
 * @param props
 * @returns {JSX.Element}
 */
export const viewScheduledReportEdit = (props) => (
    <Edit
        {...props} title={<EditTitle/>} desc={<EditDescription/>}
        actions={<EditActions/>} basePath="/ScheduledReports"
    >
        <SimpleForm>
            {/* Details */}
            <hr style={viewStyles.divider}/>
            <span style={viewStyles.fieldHeader}>Scheduled Report Details</span>
            <hr style={viewStyles.break}/>
            <TextInput source="name" label="Report Name" validate={[required]}
                       style={viewStyles.rowField}
            />
            <FormattedDateTextInput
                formType="edit"
                name="expires"
                source="expires"
                label="Expires"
                placeholder="MM/DD/YYYY"
                isWide={false}
                style={{...viewStyles.rowField, ...viewStyles.calendarTopMargin}}
            />

            {/* Date Range Type */}
            <hr style={viewStyles.divider}/>
            <span style={viewStyles.fieldHeader}>Report Date Range</span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            <span style={viewStyles.fieldDescription}>
                Select a range type: a specific range of dates, or a date relative to the Report's
                run time.
                <TooltipIcon dataFor="ttDateRangeType"/>
                <ReactTooltip id="ttDateRangeType" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    <ul>
                        <li>
                            <strong>Specific Start/End Dates</strong> use the same Start Date and End
                            Date each time the Report runs. If the Report runs before the End Date, it
                            will encompass data from the Start Date to report runtime.
                        </li>
                        <li>
                            <strong>Relative to Report Runtime</strong> covers a span of time back from
                            each time the Report runs. The report will include data from the End Date
                            to a set amount of days, weeks or months back, to a maximum of one year.
                        </li>
                    </ul>
                </ReactTooltip>
            </span>
            <hr style={viewStyles.break}/>
            <SelectInput source="date_range_type" label="Date Range Type"
                         choices={[
                             {id: 'specific', name: 'Specific Start/End Dates'},
                             {id: 'relative', name: 'Relative to Report Runtime'}
                         ]}
                         style={viewStyles.fieldMedium}
            />
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            {/* Date Range: Specific */}
            <DependentInput dependsOn="date_range_type" value="specific">
                <span style={viewStyles.fieldDescription}>
                    Select specific Start and End dates and a time zone offset.
                </span>
                <hr style={viewStyles.break}/>

                <FormattedDateTextInput
                    formType="edit"
                    name="from_day"
                    source="from_day"
                    label="First Date in Range"
                    placeholder="MM/DD/YYYY"
                    isWide={false}
                    style={{...viewStyles.rowField, ...viewStyles.calendarTopMargin}}
                />
                <FormattedDateTextInput
                    formType="edit"
                    name="to_day"
                    source="to_day"
                    label="Last Date in Range"
                    placeholder="MM/DD/YYYY"
                    isWide={false}
                    style={{...viewStyles.rowField, ...viewStyles.calendarTopMargin}}
                />
                <SelectInput source="payload.dateRange.tz" label="Time Zone Offset"
                             choices={TIME_ZONE_OFFSETS}
                             style={viewStyles.rowField}
                />
            </DependentInput>

            {/* Date Range: Relative */}
            <DependentInput dependsOn="date_range_type" value="relative">
                <span style={viewStyles.fieldDescription}>
                    Select a span of time before the Report's runtime and a time zone offset.
                    Maximum time is 1 year.
                </span>
                <hr style={viewStyles.break}/>
                <ScheduleReportFormDaysBackInput formName="record-form" style={viewStyles.rowFieldHalf}/>
                <SelectInput source="payload.dateRange.tz" label="Time Zone Offset"
                             choices={TIME_ZONE_OFFSETS}
                             style={viewStyles.rowField}
                />
            </DependentInput>

            {/* Secondary Filters */}
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            <DependentInput dependsOn="filter_types" resolve={hasSecondaryFilter}>
                <span style={viewStyles.fieldDescription}>
                    This Report has additional filters to narrow results:
                </span>
                <ShowFilterFields/>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
                <span style={viewStyles.fieldDescription}>
                    If desired, modify or remove the additional filters below. Any selection will
                    override current filters.
                </span>
            </DependentInput>
            <DependentInput dependsOn="filter_types" resolve={lacksSecondaryFilter}>
                <span style={viewStyles.fieldDescription}>
                    Select an optional secondary filter to narrow results.
                </span>
            </DependentInput>
            <SecondaryFilterFieldsWithLocations
                type="edit"
                displayFields={true}
                formName="record-form"
                includeLocationSubmit={false}
            />
            <hr style={viewStyles.break}/>

            {/* Frequency */}
            <hr style={viewStyles.divider}/>
            <span style={viewStyles.fieldHeader}>Report Generated At:</span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            <span style={viewStyles.fieldDescription}>
                Frequency and time this Report is run. If Frequency is weekly, select a weekday.
            </span>
            <hr style={viewStyles.break}/>
            <SelectInput source="frequency" label="Frequency" validate={[required]}
                         choices={[
                             {id: 'weekly', name: 'Weekly'},
                             {id: 'daily', name: 'Daily'},
                         ]}
                         style={viewStyles.rowField}
            />
            <DependentInput dependsOn="frequency" value="weekly" style={viewStyles.rowField}>
                <SelectInput source="report_day" label="Weekday"
                             choices={WEEKDAYS}
                />
            </DependentInput>
            <SelectInput source="run_time_hour" label="Hour"
                         choices={HOURS_24_AMPM}
                         style={viewStyles.rowField}
            />

            {/* Emails */}
            <hr style={viewStyles.divider}/>
            <span style={viewStyles.fieldHeader}>Recipients</span>
            <hr style={viewStyles.break}/>
            <LongTextInput source="emails_str" label="Emails"
                           style={viewStyles.fieldWide}
                           validate={[required, validateEmailArray]}
            />

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the Scheduled Reports List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Edit>
);



// WEBPACK FOOTER //
// ./src/Fuse/views/ScheduledReport.js